package com.snakesworld.game.navigation

import androidx.compose.runtime.Composable
import androidx.navigation3.runtime.NavKey

/**
 * Android's [android.view.View.setSystemGestureExclusionRects] cap, in dp.
 * Taller rects are ignored above this, and the system keeps the bottom portion.
 */
internal const val MATCH_SYSTEM_GESTURE_EXCLUSION_MAX_HEIGHT_DP = 200

/**
 * Minimum edge width to exclude. Wider than the usual ~24dp gesture inset so the
 * band reaches the joystick / D-pad and the boost button, which sit on the lower edges.
 */
internal const val MATCH_SYSTEM_GESTURE_EXCLUSION_MIN_EDGE_DP = 48

/**
 * One system-gesture exclusion rectangle, in pixels, relative to the root view.
 */
internal data class GestureExclusionRect(
    val left: Int,
    val top: Int,
    val right: Int,
    val bottom: Int,
)

/**
 * iOS edge-swipe back and Android system / predictive back are ignored while a match
 * is the top destination, including after game over. Game over stays on [InGameRoute]
 * (the overlay is not its own route), so platform back stays a no-op there too.
 * Leave through the in-game back button and its ABORT MISSION? dialog.
 * Every other route keeps the normal pop.
 */
fun shouldBlockPlatformBack(top: NavKey?): Boolean = top is InGameRoute

/**
 * Bottom-aligned side bands for [android.view.View.setSystemGestureExclusionRects].
 *
 * Android honors about [MATCH_SYSTEM_GESTURE_EXCLUSION_MAX_HEIGHT_DP] of vertical
 * exclusion per edge and keeps the bottom of a taller request. These rects spend
 * that budget at the bottom of the view, where the D-pad / joystick and the boost
 * button sit. A full-height rect would be clipped to the same bottom band.
 *
 * Returns an empty list when the view has not been laid out.
 */
internal fun matchEdgeExclusionRects(
    viewWidthPx: Int,
    viewHeightPx: Int,
    edgePx: Int,
    maxHeightPx: Int,
): List<GestureExclusionRect> {
    if (viewWidthPx <= 0 || viewHeightPx <= 0 || edgePx <= 0 || maxHeightPx <= 0) {
        return emptyList()
    }
    val edge = edgePx.coerceAtMost(viewWidthPx / 2)
    if (edge <= 0) return emptyList()
    val height = maxHeightPx.coerceAtMost(viewHeightPx)
    val top = viewHeightPx - height
    return listOf(
        GestureExclusionRect(left = 0, top = top, right = edge, bottom = viewHeightPx),
        GestureExclusionRect(
            left = viewWidthPx - edge,
            top = top,
            right = viewWidthPx,
            bottom = viewHeightPx,
        ),
    )
}

/**
 * Installs the platform back policy around [androidx.navigation3.ui.NavDisplay].
 *
 * On iOS this disables the navigation-event dispatcher for the whole display while
 * [blockSystemBack] is true, which turns off the edge-pan recognizer so arena swipes
 * at the screen edge still reach the canvas. Android and desktop/web pass [content]
 * through unchanged; Android consumes the gesture from the match screen instead.
 */
@Composable
expect fun MatchSystemBackPolicy(
    blockSystemBack: Boolean,
    content: @Composable () -> Unit,
)

/**
 * Called from the in-game screen, which is composed after `NavDisplay`'s own back handler.
 *
 * Android registers a later handler that swallows the event, so the display never starts
 * its predictive pop. iOS must not register an enabled handler here: that would turn the
 * edge-pan recognizer back on and steal arena swipes.
 */
@Composable
expect fun ConsumeMatchSystemBack()
